import { ProjectStage } from '@shared/enums/project-stage.enum';
import { TaskType } from '@shared/enums/task-type.enum';
import { Task } from '@shared/models/task.model';
import { TaskEmailVariant, TaskEmailVariantType } from '@shared/models/task.model';

export interface ProcessMessageSidenavData {
  mode?: 'create' | 'send' | 'edit';
  projectId: number;
  stage: ProjectStage;
  sortOrder: number;
  parentId: number | null;
  groupId?: number | null;
  mapperTaskId?: number | null;
  task?: Task | null;
  taskType?: TaskType | string | null;
}

export interface EmailMapperToken {
  key: string;
  label: string;
  value: string;
}

export interface NormalizedAssignments {
  assignedToIds: number[];
  assignedEmails: string[];
}

export interface InitialFormState {
  templateName: string;
  projectId: number | null;
  projectName: string;
  to: string | null;
  bcc: string[];
  subject: string;
  content: string;
  assignedToIds: number[];
  assignedEmails: string[];
  assignmentSignature: string;
  initialEmails: any[];
  assignedToInitialValue: any[];
}

export function isProcessMessageLikeTaskType(
  type: string | null | undefined,
): boolean {
  return type === TaskType.TYPE_MESSAGE || type === TaskType.TYPE_FOLLOW_UP;
}

export function normalizeAssignedValues(
  assignedValue: unknown,
): NormalizedAssignments {
  const normalizedAssigned = Array.isArray(assignedValue) ? assignedValue : [];

  return {
    assignedToIds: normalizedAssigned
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0),
    assignedEmails: normalizedAssigned
      .map((value) => String(value))
      .filter((value) => value.includes('@')),
  };
}

export function buildAssignmentSignature(
  assignedToIds: number[],
  assignedEmails: string[],
): string {
  return JSON.stringify({
    assignedToIds: [...assignedToIds].sort((a, b) => a - b),
    assignedEmails: [...assignedEmails].sort((a, b) => a.localeCompare(b)),
  });
}

export function parseEmailList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return String((item as any).email || (item as any).id || '').trim();
        }
        return '';
      })
      .map((email) => email.trim())
      .filter(Boolean);
  }

  const raw = String(value || '').trim();
  if (!raw) return [];

  return raw
    .split(/[;,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeEmailMapperToken(item: any): EmailMapperToken | null {
  if (!item) return null;

  if (typeof item === 'string') {
    return { key: item, label: item, value: item };
  }

  const key =
    item.key ||
    item.placeholder ||
    item.token ||
    item.variable ||
    item.mapper ||
    item.value;
  const value =
    item.value ||
    item.mapper ||
    item.token ||
    item.variable ||
    item.placeholder ||
    item.mapValue ||
    item.key;
  const label = item.label || item.name || item.title || key || value;

  if (!key) return null;

  return {
    key: String(key),
    label: String(label),
    value: String(value || key),
  };
}

export function normalizeEmailMappersResponse(response: any): EmailMapperToken[] {
  const source = response?.records || response?.mappers || response?.data || response || [];

  if (Array.isArray(source)) {
    return source
      .map((item) => normalizeEmailMapperToken(item))
      .filter((item): item is EmailMapperToken => !!item?.key);
  }

  if (source && typeof source === 'object') {
    return Object.entries(source)
      .map(([key, value]) => {
        if (typeof value === 'string') {
          return { key, label: key, value };
        }

        if (value && typeof value === 'object') {
          return normalizeEmailMapperToken({ key, ...(value as object) });
        }

        return { key, label: key, value: key };
      })
      .filter((item): item is EmailMapperToken => !!item?.key);
  }

  return [];
}

export function normalizeEmailVariantType(
  type: string | null | undefined,
): TaskEmailVariantType | null {
  if (type === TaskEmailVariantType.COURTESY || type === TaskEmailVariantType.DIRECT) {
    return type;
  }

  return null;
}

export function resolveAvailableEmailVariantType(
  emailVariants: TaskEmailVariant[],
  preferredType: TaskEmailVariantType,
): TaskEmailVariantType {
  const availableTypes = emailVariants
    .map((variant) => normalizeEmailVariantType(variant.type))
    .filter((type): type is TaskEmailVariantType => !!type);

  if (availableTypes.includes(preferredType)) {
    return preferredType;
  }

  return availableTypes[0] || TaskEmailVariantType.COURTESY;
}

export function getSelectedEmailVariant(
  emailVariants: TaskEmailVariant[],
  selectedType: TaskEmailVariantType,
): TaskEmailVariant | null {
  if (!emailVariants.length) return null;

  return (
    emailVariants.find((variant) => variant.type === selectedType) ||
    emailVariants[0] ||
    null
  );
}

export function syncEmailVariantsWithCurrentValues(
  emailVariants: TaskEmailVariant[],
  selectedType: TaskEmailVariantType,
  title: string,
  content: string,
): TaskEmailVariant[] {
  const currentVariant = getSelectedEmailVariant(emailVariants, selectedType);
  if (!currentVariant) {
    return emailVariants;
  }

  return emailVariants.map((variant) =>
    variant.type === currentVariant.type
      ? {
          ...variant,
          title,
          content,
        }
      : variant,
  );
}

export function resolveProcessMessageTaskType(
  sources: Array<TaskType | string | null | undefined>,
): TaskType | string {
  const type = String(sources.find((value) => String(value || '').trim()) || '').trim();
  return isProcessMessageLikeTaskType(type) ? type : TaskType.TYPE_MESSAGE;
}

export function buildInitialFormState(params: {
  task: Task;
  projectId: number;
  isEditMode: boolean;
  isSendMode: boolean;
  mapAssignedUser: (user: any) => any;
  mapAssignedEmail: (email: string) => any;
}): InitialFormState {
  const { task, projectId, isEditMode, isSendMode, mapAssignedUser, mapAssignedEmail } = params;

  const to = String(task?.assignedEmails?.[0] || '');
  const projectData: any = task?.project || {};
  const basicData: any = projectData?.basicDataBox || {};
  const projectName = [
    projectData?.name,
    projectData?.clientData?.name,
    basicData?.address || basicData?.addressStreet,
  ]
    .filter(Boolean)
    .join(' | ');

  const taskAny: any = task || {};
  const emailTitle = String(taskAny?.emailTitle || task?.description || '');
  const emailContent = String(taskAny?.emailContent || task?.content || '');
  const description = String(task?.description || '');
  const internalNote = String(task?.internalNote || '');
  const hasMultipleEmailVariants =
    Array.isArray(task?.emailVariants) && task.emailVariants.length > 1;
  const initialSubject = hasMultipleEmailVariants
    ? ''
    : emailTitle || (isEditMode ? internalNote : description);
  const initialContent = hasMultipleEmailVariants ? '' : emailContent;
  const assignedUserInitial = Array.isArray(task?.assignedUsers)
    ? task.assignedUsers.map((user: any) => mapAssignedUser(user))
    : [];
  const assignedEmailInitial = Array.isArray(task?.assignedEmails)
    ? task.assignedEmails.map((email: string) => mapAssignedEmail(email))
    : [];
  const assignedUserValues = assignedUserInitial
    .map((item: any) => Number(item?.id))
    .filter((value) => Number.isFinite(value) && value > 0);
  const assignedEmailValues = assignedEmailInitial.map((item: any) => item?.id).filter(Boolean);

  let initialEmails: any[] = [];
  if (isSendMode) {
    const firstAssigned =
      (task as any)?.assignedTo?.[0] || (task as any)?.assignedUsers?.[0] || null;
    const recipientLabel =
      firstAssigned?.userPerson?.previewName ||
      firstAssigned?.previewName ||
      firstAssigned?.name ||
      to;
    initialEmails = to
      ? [{ id: to, email: to, fullName: recipientLabel, name: recipientLabel }]
      : [];
  }

  return {
    templateName: isEditMode ? description : internalNote,
    projectId: task?.project?.id || projectId || null,
    projectName,
    to: to || null,
    bcc: [],
    subject: initialSubject,
    content: initialContent,
    assignedToIds: assignedUserValues,
    assignedEmails: Array.isArray(task?.assignedEmails) ? task.assignedEmails : [],
    assignmentSignature: buildAssignmentSignature(
      [...assignedUserValues],
      [...assignedEmailValues],
    ),
    initialEmails,
    assignedToInitialValue: [...assignedUserInitial, ...assignedEmailInitial],
  };
}
